import { Injectable } from "@angular/core";
import { HttpClient, HttpResponse } from "@angular/common/http";

import { Observable, throwError } from "rxjs";

import { environment } from "@env/environment";
import { SearchString } from "@model/SearchString";
import { SearchType } from "@model/SearchType";
import { PagedData } from "@model/datatable/paged-data";
import { Topic } from "@model/Topic";
import { catchError } from "rxjs/operators";

export interface DataExtraction {
  selected_studies: any[];
  extraction_questions: any[];
}

@Injectable({ providedIn: "root" })
export class DevelopmentService {
  constructor(private http: HttpClient) {}

  storeSearchString(formData: SearchString): Observable<SearchString> {
    return this.http.post<SearchString>(
      `${environment.base_url}/projects/developments/search_strings`,
      formData
    );
  }

  updateSearchString(
    id: number,
    formData: SearchString
  ): Observable<SearchString> {
    return this.http.put<SearchString>(
      `${environment.base_url}/projects/developments/search_strings/${id}`,
      formData
    );
  }

  deleteSearchString(id: number): Observable<any> {
    return this.http.delete(
      `${environment.base_url}/projects/developments/search_strings/${id}`
    );
  }

  bulkStoreSelectedStudies(formData: any): Observable<any> {
    return this.http.post<any>(
      `${environment.base_url}/projects/developments/selected_studies/bulk`,
      formData
    );
  }

  bulkIncrementSelectedStudies(formData: any): Observable<any> {
    return this.http.post<any>(
      `${environment.base_url}/projects/developments/selected_studies/bulk_bibcontent`,
      formData
    );
  }

  bulkIncrementBibtexSelectedStudies(formData: any): Observable<any> {
    return this.http.post<any>(
      `${environment.base_url}/projects/developments/selected_studies/bulk_bibtex`,
      formData
    );
  }

  uploadFile(search_string_id: number, file: File) {
    const payload = new FormData();

    payload.append("file", file);
    payload.append("search_string_id", JSON.stringify(search_string_id));

    return this.http.post(
      `${environment.base_url}/projects/developments/search_strings/upload`,
      payload,
      {
        reportProgress: true,
        observe: "events",
      }
    );
  }

  downloadFile(search_string_id: number): Observable<HttpResponse<Blob>> {
    const url = `${environment.base_url}/projects/developments/search_strings/download_importedfile/${search_string_id}`;
    return this.http.get<Blob>(url, {
      observe: "response",
      responseType: "blob" as "json",
    });
  }

  exportStudies(searchStringId: number, format: string): Observable<any> {
    const url = `${environment.base_url}/projects/developments/search_strings/export/${searchStringId}/${format}`;
    return this.http.get(url);
  }

  bulkDeleteStudies(ids: number[]): Observable<any> {
    return this.http.post<any>(
      `${environment.base_url}/projects/developments/selected_studies/bulk_delete`,
      ids
    );
  }

  updateAbstract(formData: any): Observable<any> {
    return this.http.post<any>(
      `${environment.base_url}/projects/developments/selected_studies/abstractUpdate`,
      formData
    );
  }

  updateStudies(form: any): Observable<any> {
    return this.http.post<any>(
      `${environment.base_url}/projects/developments/selected_studies/bulk_update`,
      form
    );
  }

  paginateSelectedStudiesBySearchString(
    projectId: number,
    per_page: number = 1,
    page: number = 1,
    development_search_string_id: string = "",
    isExtractionQuestions: boolean = false,
    quest: string = ""
  ): Observable<any> {
    return this.http.get<any>(
      `${environment.base_url}/projects/${projectId}` +
        `/annotations${
          development_search_string_id && "/" + development_search_string_id
        }?` +
        `page=${page}&per_page=${per_page}${
          isExtractionQuestions
            ? "&extraction_questions=" + isExtractionQuestions
            : ""
        }` +
        `&quest=${quest}`
    );
  }

  storeSearchType(projectId: number, formData: SearchType): Observable<any> {
    return this.http.post<any>(
      `${environment.base_url}/projects/${projectId}/settings/setting_type_searchs`,
      formData
    );
  }

  getSearchTypes(projectId: number): Observable<Array<SearchType>> {
    return this.http.get<Array<SearchType>>(
      `${environment.base_url}/projects/${projectId}/settings/setting_type_searchs`
    );
  }

  getAllStudies(
    projectId: number,
    search_strings: string[]
  ): Observable<Array<any>> {
    return this.http.post<Array<any>>(
      `${environment.base_url}/projects/${projectId}/selected_studies/get_all`,
      { search_strings }
    );
  }

  paginateAllStudies(
    projectId: number,
    per_page: number,
    page: number = 1,
    status: string = "",
    blind_selection_status: string = "",
    development_search_string_id: string = "",
    search_strings: string[]
  ): Observable<PagedData<any>> {
    return this.http.post<PagedData<any>>(
      `${environment.base_url}/projects/${projectId}/selected_studies${
        development_search_string_id && "/" + development_search_string_id
      }?per_page=${per_page}&page=${page}${status ? `&status=${status}` : ""}${
        blind_selection_status
          ? `&blind_selection_status=${blind_selection_status}`
          : ""
      }`,
      { search_strings }
    );
  }

  paginateStudiesDuplicates(projectId: number): Observable<PagedData<any>> {
    return this.http.get<PagedData<any>>(
      `${environment.base_url}/projects/${projectId}/selected_studies/find_duplicates`
    );
  }

  getLiteratureRevisionStudies(projectId: number): Observable<any> {
    return this.http.get(
      `${environment.base_url}/projects/${projectId}/literature_revision_studies`
    );
  }

  getLiteratureRevision(projectId: number): Observable<any> {
    return this.http.get(
      `${environment.base_url}/projects/${projectId}/literature_revision`
    );
  }

  saveOrUpdateLiteratureRevisions(
    projectId: number,
    topics: Array<Topic>,
    literature_revision_id: number = null
  ): Observable<any> {
    return this.http.put(
      `${environment.base_url}/projects/developments/literature_revisions${
        literature_revision_id ? "/" + literature_revision_id : ""
      }`,
      {
        project_id: projectId,
        topics,
      }
    );
  }

  rejectAllStudies(search_string_id: number): Observable<any> {
    return this.http.post<any>(
      `${environment.base_url}/projects/developments/selected_studies/reject_all_studies`,
      { search_string_id }
    );
  }

  getAllAcceptedStudies(projectId: number): Observable<any> {
    return this.http.get<any>(
      `${environment.base_url}/projects/${projectId}/selected_studies/get_all_accepted`
    );
  }

  updateListingOrder(data: any): Observable<any> {
    return this.http.post(
      `${environment.base_url}/projects/developments/search_strings/update_listing_order`,
      data
    );
  }

  saveSelectedRevisionStudies(
    literature_revision_selecteds: any[]
  ): Observable<any> {
    return this.http.post(
      `${environment.base_url}/projects/developments/literature_revision_selecteds`,
      {
        literature_revision_selecteds,
      }
    );
  }

  uploadNbib(file: File): Observable<any> {
    const formData = new FormData();
    formData.append("files", file);

    return this.http.post<any>(
      `${environment.api_convert_bib_url}/upload`,
      formData
    ).pipe(
      catchError(error => {
        console.error('Erro no serviço:', error);
        return throwError(error);
      })
    );
  }

  getBibFile(): Observable<Blob> {
    const downloadUrl = `${environment.api_convert_bib_url}/download`;
  
    return this.http.get(downloadUrl, { responseType: 'blob' })
      .pipe(
        catchError(error => {
          console.error('Erro no serviço:', error);
          return throwError(error);
        })
      );
  }
}
